/**
 * Clean redraw and Line map: the drawing (SVG markup in sheet coordinates) plus the
 * text candidates the label pass places on screen.
 */
import { type Box, type Model, PROC, SIGK, connSyms, isTagBubble, lineEnds, lineRuns, polyMid, procDeg, snap } from './model'
import { glyphOf, r1 } from './glyphs'
import { sheetText } from './labels'
import { FAMILY, lineAttrs, sizeWidth } from './lineService'

export interface CleanOptions { signals: boolean; fittings: boolean; notes: boolean; attributes: boolean; connectionPoints: boolean }
export interface LineMapOptions { colorBy: 'contents' | 'line'; weightBySize: boolean }
export interface LegendRow { key: string; label: string; detail: string; color: string; count: number; unit: 'line' | 'segment' }
export interface Legend { title: string; rows: LegendRow[]; sizes: Array<{ sizeIn: number; width: number }> }
export interface ViewLayout { svg: string; bounds: Box; glyphBoxes: Map<number, Box>; legend?: Legend; textCount: number }


const pathD = (lines: number[][][]) => lines.map(l => 'M' + l.map(p => p[0] + ',' + p[1]).join('L')).join('')
const isLeader = (m: Model, c: number) => connSyms(m, c).some(s => isTagBubble(m, s))
const INLINE = new Set(['valve', 'cv', 'fe', 'fit', 'tee'])

function paperRect(m: Model): Box {
  let B: Box = [Infinity, Infinity, -Infinity, -Infinity]
  for (const i of [...m.SYMS, ...m.CONNS, ...m.TAGS]) { const b = m.nodes[i].bb; B = [Math.min(B[0], b[0]), Math.min(B[1], b[1]), Math.max(B[2], b[2]), Math.max(B[3], b[3])] }
  const g = 220
  return [B[0] - g, B[1] - g, B[2] + g, B[3] + g]
}
const paperSVG = (P: Box) => `<rect class="paper" x="${r1(P[0])}" y="${r1(P[1])}" width="${r1(P[2] - P[0])}" height="${r1(P[3] - P[1])}" rx="24"/>`

/** where a pipe end attached to symbol s should reach: the centre of an inline symbol, else the nearest point of its drawn box */
function stubTarget(m: Model, s: number, p: number[], GB: Map<number, Box>): number[] {
  const K = m.KIND[s], gb = GB.get(s)
  if (K && INLINE.has(K) && !isTagBubble(m, s)) { const sp = snap(m, s); return [sp.x, sp.y] }
  if (!gb) { const b = m.nodes[s].bb; return [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2] } // hidden symbol (a note on a signal line): bridge through it
  return [Math.max(gb[0], Math.min(gb[2], p[0])), Math.max(gb[1], Math.min(gb[3], p[1]))]
}
/** a red ring around drawn symbols the parse left with no line at all */
function noLineRings(m: Model, GB: Map<number, Box>): string {
  let s = ''
  for (const [i, gb] of GB) {
    const K = m.KIND[i]
    if (K === 'note' || K === 'equip' || K === 'tee') continue
    if ([...m.nbr[i]].some(j => m.nodes[j].k === 'c')) continue
    const p = 10
    s += `<rect class="iso-bad" data-i="${i}" x="${r1(gb[0] - p)}" y="${r1(gb[1] - p)}" width="${r1(gb[2] - gb[0] + 2 * p)}" height="${r1(gb[3] - gb[1] + 2 * p)}" rx="${r1(Math.min(gb[2] - gb[0], gb[3] - gb[1]) / 2 + p)}"><title>No line attached</title></rect>`
  }
  return s
}
/** every attached pipe end continues into its symbol, so a stored link is always drawn connected */
function pipeStubs(m: Model, GB: Map<number, Box>, attrsOf: (c: number) => string | null): string {
  let s = ''
  for (const c of m.CONNS) {
    const at = attrsOf(c); if (at == null) continue
    for (const e of lineEnds(m, c)) {
      if (!e.ok) continue
      const q = stubTarget(m, e.s, e.p, GB)
      if (Math.hypot(q[0] - e.p[0], q[1] - e.p[1]) < 1) continue
      s += `<path ${at} data-i="${c}" d="M${e.p[0]},${e.p[1]}L${r1(q[0])},${r1(q[1])}"/>`
    }
  }
  return s
}
function connPoints(m: Model, onlyProc: boolean): string {
  let s = ''
  for (const c of m.CONNS) {
    if (onlyProc && !PROC.has(m.nodes[c].lk!)) continue
    for (const e of lineEnds(m, c)) s += e.ok ? `<path class="cp" data-i="${c}" d="M${e.p[0]},${e.p[1]}l0,0.01"/>`
      : `<path class="cp-bad" data-i="${c}" d="M${e.p[0]},${e.p[1]}l0,0.01"/><path class="cp-bad-in" data-i="${c}" d="M${e.p[0]},${e.p[1]}l0,0.01"/>`
  }
  return s
}

function flowArrows(m: Model, c: number): string {
  const n = m.nodes[c]
  if (!m.connUp[c].size || !m.connDown[c].size) return ''
  const mm = polyMid(n.ln!); if (!mm || mm.len < 160) return ''
  const tg = m.nodes[[...m.connDown[c]][0]].bb, tx = (tg[0] + tg[2]) / 2, ty = (tg[1] + tg[3]) / 2, e0 = mm.line[0], e1 = mm.line[mm.line.length - 1]
  let dx = mm.dx, dy = mm.dy
  if (Math.hypot(e0[0] - tx, e0[1] - ty) < Math.hypot(e1[0] - tx, e1[1] - ty)) { dx = -dx; dy = -dy }
  const S = 18, px = -dy, py = dx
  return `<path class="parr" data-i="${c}" d="M${r1(mm.x + dx * S)},${r1(mm.y + dy * S)}L${r1(mm.x - dx * S + px * S * 0.7)},${r1(mm.y - dy * S + py * S * 0.7)}L${r1(mm.x - dx * S - px * S * 0.7)},${r1(mm.y - dy * S - py * S * 0.7)}z"/>`
}

export function buildClean(m: Model, o: CleanOptions): ViewLayout {
  const P = paperRect(m)
  let s = paperSVG(P)
  const pipeClass = (c: number) => { const n = m.nodes[c], sig = SIGK.has(n.lk!); return `pipe ${isLeader(m, c) ? 'leader' : sig ? 'sig' : 'proc'} lk-${n.lk}` }
  for (const c of m.CONNS) {
    const n = m.nodes[c], sig = SIGK.has(n.lk!)
    if (sig && !o.signals) continue
    s += `<path class="${pipeClass(c)}" data-i="${c}" d="${pathD(n.ln!)}"/>`
    if (!sig && !isLeader(m, c) && o.fittings) s += flowArrows(m, c)
  }
  const GB = new Map<number, Box>()
  let gl = ''
  for (const i of m.SYMS) {
    const K = m.KIND[i]
    if ((K === 'note' && !o.notes) || (K === 'fit' && !o.fittings)) continue
    const G = glyphOf(m, i); if (!G.gb) continue
    gl += G.g; GB.set(i, G.gb)
  }
  s += pipeStubs(m, GB, c => (SIGK.has(m.nodes[c].lk!) && !o.signals) ? null : `class="${pipeClass(c)}"`)
  s += gl
  if (o.connectionPoints) {
    s += connPoints(m, !o.signals) + noLineRings(m, GB)
  }
  const text = sheetText(m, { attrs: o.attributes, notes: o.notes, lineDrawn: c => o.signals || !SIGK.has(m.nodes[c].lk!) }, GB)
  return { svg: s + text.svg, bounds: [P[0] - 60, P[1] - 60, P[2] + 60, P[3] + 60], glyphBoxes: GB, textCount: text.count }
}

/**
 * Line map: each pipe run in one colour with its line number. Colour is what the
 * pipe carries (its service code, decoded) or, optionally, one colour per line
 * number; line weight is the pipe size.
 */
export function buildLines(m: Model, lo: LineMapOptions = { colorBy: 'contents', weightBySize: true }): ViewLayout {
  const R = lineRuns(m), P = paperRect(m)
  const look = new Map(R.lines.map(r => {
    const a = lineAttrs(r.id)
    return [r.id, { a, color: lo.colorBy === 'contents' ? a.color : r.color, w: lo.weightBySize ? sizeWidth(a.sizeIn) : 5.5 }]
  }))
  const lineStyle = (L: string) => { const x = look.get(L)!; return `style="stroke:${x.color};stroke-width:${r1(x.w)}px"` }
  let s = paperSVG(P), cas = '', col = ''
  for (const c of m.CONNS) {
    const n = m.nodes[c], d = pathD(n.ln!)
    if (SIGK.has(n.lk!)) { s += `<path class="pipe msig" data-i="${c}" d="${d}"/>`; continue }
    const L = R.lineOf.get(c)
    if (!L) { s += `<path class="pipe mun" data-i="${c}" d="${d}"/>`; continue }
    cas += `<path class="pipe mcas" data-i="${c}" style="stroke-width:${r1(look.get(L)!.w + 3.5)}px" d="${d}"/>`
    col += `<path class="pipe mline" data-i="${c}" ${lineStyle(L)} d="${d}"/>`
  }
  s += cas + col
  const GB = new Map<number, Box>()
  let gl = ''
  for (const i of m.SYMS) {
    const K = m.KIND[i]; if (K === 'note') continue
    // instruments and signal tees that sit on no process line belong to the signal side; everything
    // else is drawn, including process items the parse left unattached (they get the red ring)
    if (procDeg(m, i) === 0 && (K === 'inst' || K === 'tee' || isTagBubble(m, i))) continue
    const G = glyphOf(m, i); if (!G.gb) continue
    gl += G.g.replace('class="gl ', 'class="gl mglyph '); GB.set(i, G.gb)
  }
  s += pipeStubs(m, GB, c => {
    const n = m.nodes[c]; if (SIGK.has(n.lk!)) return 'class="pipe msig"'
    const L = R.lineOf.get(c)
    return L ? `class="pipe mline" ${lineStyle(L)}` : 'class="pipe mun"'
  })
  s += gl + noLineRings(m, GB)
  const text = sheetText(m, { attrs: false, notes: false, lineDrawn: c => PROC.has(m.nodes[c].lk!), pill: c => { const L = R.lineOf.get(c); return L ? look.get(L)!.color : null } }, GB)
  return { svg: s + text.svg, bounds: [P[0] - 60, P[1] - 60, P[2] + 60, P[3] + 60], glyphBoxes: GB, legend: lineLegend(m, R, look, lo), textCount: text.count }
}

/** what the colours and weights mean on this sheet */
function lineLegend(m: Model, R: ReturnType<typeof lineRuns>, look: Map<string, { a: ReturnType<typeof lineAttrs>; color: string; w: number }>, lo: LineMapOptions): Legend {
  const rows: LegendRow[] = []
  if (lo.colorBy === 'contents') {
    const by = new Map<string, LegendRow & { len: number }>()
    for (const r of R.lines) {
      const { a, color } = look.get(r.id)!, key = a.service ?? '?'
      let row = by.get(key)
      if (!row) by.set(key, (row = { key, label: a.service ?? 'Other shape', color, count: 0, len: 0, unit: 'line',
        detail: a.fluid ?? (a.service ? FAMILY.unknown.label : 'Line number in another format') }))
      row.count++; row.len += r.len
    }
    rows.push(...[...by.values()].sort((x, y) => y.len - x.len))
  }
  const none = m.CONNS.filter(c => PROC.has(m.nodes[c].lk!) && !R.lineOf.has(c)).length
  if (none) rows.push({ key: '-', label: 'Grey', detail: 'No line number reached these pipes', color: '', count: none, unit: 'segment' })
  const sizes = lo.weightBySize ? [...new Set(R.lines.map(r => look.get(r.id)!.a.sizeIn).filter((x): x is number => x != null))].sort((a, b) => a - b) : []
  return { title: lo.colorBy === 'contents' ? 'Colour = what the pipe carries' : 'Colour = line number', rows, sizes: sizes.map(sizeIn => ({ sizeIn, width: sizeWidth(sizeIn) })) }
}
